/**
 * Fuente ÚNICA de la "ficha de características" iconográfica de un hechizo: convierte un
 * `SpellDefinition` en una lista ordenada de filas `{ icon, text }` donde cada fila
 * representa una característica (escuela de daño, cura, costos, casteo, cooldown, objetivo,
 * rango, ÁREA/AoE, control). Los nombres de icono resuelven contra el atlas del cliente
 * (`sprites/IconAtlases.ts`, `createUIIcon`/`getIconTexture`).
 *
 * La usan TANTO la pantalla de hechizos (`SpellsPanel`) COMO el vendedor de hechizos
 * (`SpellVendorPanel`), así ambas muestran la MISMA iconografía y se mantienen en sync.
 * Ver docs/SPELLS.md y docs/VENDORS_AND_NPC_INTERACTION.md.
 */
import type { SpellDefinition, SpellTargetType, SpellDamageType } from '@shared/types/spell';

export interface SpellInfoRow {
  icon: string;
  text: string;
}

export const TARGET_TYPE_LABELS: Record<SpellTargetType, string> = {
  self: 'Uno mismo',
  players: 'Aliados',
  npcs: 'Enemigos',
  players_and_npcs: 'Aliados y enemigos',
  ground: 'Tile (suelo)',
};

export const TARGET_TYPE_ICON: Record<SpellTargetType, string> = {
  self: 'target_self',
  players: 'target_ally',
  npcs: 'target_enemy',
  players_and_npcs: 'target_enemy',
  ground: 'target_ground',
};

export const RESOURCE_ICON: Record<string, string> = {
  mana: 'mana',
  hp: 'hp',
  stamina: 'stamina',
};

const RESOURCE_LABEL: Record<string, string> = { mana: 'Maná', stamina: 'Energía', hp: 'HP' };

const DAMAGE_SCHOOL_ICON: Record<SpellDamageType, string> = {
  physical: 'physical_damage',
  magical: 'magic_damage',
  pure: 'magic_crit',
};
const DAMAGE_SCHOOL_LABEL: Record<SpellDamageType, string> = {
  physical: 'físico',
  magical: 'mágico',
  pure: 'puro',
};

const STATUS_LABEL: Record<string, string> = {
  poison: 'Veneno',
  burn: 'Quema',
  freeze: 'Congela',
  silence: 'Silencia',
  slow: 'Ralentiza',
  bleed: 'Sangra',
  blind: 'Ciega',
  immobilize: 'Inmoviliza',
  paralyze: 'Paraliza',
};

/** Formatea un costo de recurso a texto corto (respeta flat/percent/per_second). */
function formatCost(amount: number, mode: string, resource: string): string {
  const r = RESOURCE_LABEL[resource] ?? resource;
  if (mode === 'percent') return `${Math.round(amount * 100)}% ${r}`;
  if (mode === 'per_second') return `${amount} ${r}/s`;
  return `${amount} ${r}`;
}

/**
 * Deriva las filas de características de un hechizo, en orden de lectura. Cada característica
 * presente aparece una vez; las ausentes se omiten (no hay filas vacías). Cubre el AoE de
 * forma COMPLETA: `castType` 'area' (N×N), 'screen' (pantalla entera), efecto de provocación
 * (radio) y aura (radio), no sólo `castType === 'area'`.
 */
export function getSpellInfoRows(spell: SpellDefinition): SpellInfoRow[] {
  const rows: SpellInfoRow[] = [];

  // --- Efecto principal: daño (con escuela) / cura ---
  const dmg = spell.effects.find((e) => e.type === 'damage');
  if (dmg && dmg.type === 'damage') {
    rows.push({
      icon: DAMAGE_SCHOOL_ICON[dmg.damageType],
      text: `${dmg.minDamage}–${dmg.maxDamage} daño ${DAMAGE_SCHOOL_LABEL[dmg.damageType]}`,
    });
  }
  const heal = spell.effects.find((e) => e.type === 'heal');
  if (heal && heal.type === 'heal') {
    rows.push({ icon: 'healing', text: `${heal.minHeal}–${heal.maxHeal} cura` });
  }

  // --- Costos: maná primero, después el resto ---
  const mana = spell.costs.find((c) => c.resource === 'mana');
  if (mana && mana.amount > 0) rows.push({ icon: 'mana', text: formatCost(mana.amount, mana.mode, 'mana') });
  for (const c of spell.costs) {
    if (c.resource === 'mana' || c.amount <= 0) continue;
    rows.push({ icon: RESOURCE_ICON[c.resource] ?? 'mana', text: formatCost(c.amount, c.mode, c.resource) });
  }

  // --- Casteo / cooldown ---
  rows.push({
    icon: 'cast_time',
    text: spell.castTime > 0
      ? `Casteo ${spell.castTime.toFixed(1)}s${spell.interruptible ? ' (interrumpible)' : ''}`
      : 'Instantáneo',
  });
  rows.push({ icon: 'cooldown', text: `${spell.cooldown.toFixed(1)}s recarga` });

  // --- Objetivo ---
  rows.push({
    icon: TARGET_TYPE_ICON[spell.targetType] ?? 'target_enemy',
    text: TARGET_TYPE_LABELS[spell.targetType] ?? spell.targetType,
  });

  // --- Rango (min–max) ---
  if (spell.range > 0) {
    const min = spell.minRange && spell.minRange > 0 ? spell.minRange : 0;
    rows.push({ icon: 'range', text: min > 0 ? `${min}–${spell.range} tiles` : `${spell.range} tiles` });
  }

  // --- ÁREA / AoE (completo) ---
  const areaText = getAreaText(spell);
  if (areaText) rows.push({ icon: 'area', text: areaText });

  // --- Control (status effects: inmoviliza/paraliza/veneno/etc.) ---
  const statuses = spell.effects
    .filter((e): e is Extract<typeof e, { type: 'status_effect' }> => e.type === 'status_effect')
    .map((e) => STATUS_LABEL[e.statusType] ?? e.statusType);
  if (statuses.length > 0) {
    rows.push({ icon: 'disable', text: [...new Set(statuses)].join(', ') });
  }

  return rows;
}

/** Texto del área si el hechizo es AoE por cualquier vía (castType, taunt o aura); si no, null. */
function getAreaText(spell: SpellDefinition): string | null {
  if (spell.castType === 'area' && spell.areaSize) return `${spell.areaSize}×${spell.areaSize} tiles`;
  if (spell.castType === 'screen') return 'Pantalla entera';
  const taunt = spell.effects.find((e) => e.type === 'taunt');
  if (taunt && taunt.type === 'taunt') return `Radio ${taunt.radius} tiles`;
  if (spell.aura) return `Aura · radio ${spell.aura.radiusTiles} tiles`;
  return null;
}
